/**
 * ViewSelector — Gespeicherte Filter-Ansichten (B2)
 * Quantum90 Design
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './ViewSelector.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface GespeicherteAnsicht {
  ansicht_id: number;
  bereich: string;
  name: string;
  filter_json: Record<string, unknown>;
  ist_standard: boolean;
  erstellt_am: string | null;
}

interface ViewSelectorProps {
  bereich: string;
  onFilterChange: (filter: Record<string, unknown>) => void;
  authToken: string;
  currentFilter?: Record<string, unknown>;
}

const ViewSelector: React.FC<ViewSelectorProps> = ({ bereich, onFilterChange, authToken, currentFilter }) => {
  const [ansichten, setAnsichten] = useState<GespeicherteAnsicht[]>([]);
  const [aktiveAnsichtId, setAktiveAnsichtId] = useState<number | null>(null);
  const [dropdownOffen, setDropdownOffen] = useState(false);
  const [dialogOffen, setDialogOffen] = useState(false);
  const [neuerName, setNeuerName] = useState('');
  const [istStandard, setIstStandard] = useState(false);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [speichernLaeuft, setSpeichernLaeuft] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const headers = { Authorization: `Bearer ${authToken}` };

  const ladeAnsichten = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await axios.get<GespeicherteAnsicht[]>(
        `${API_URL}/api/ansichten`,
        { headers, params: { bereich } }
      );
      setAnsichten(res.data);
      setLadeFehler(null);
    } catch {
      setLadeFehler('Ansichten konnten nicht geladen werden');
    }
  }, [authToken, bereich]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    ladeAnsichten();
  }, [ladeAnsichten]);

  // Schliesse Dropdown bei Klick ausserhalb
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOffen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const aktiveAnsicht = ansichten.find(a => a.ansicht_id === aktiveAnsichtId) ?? null;

  const ansichtWaehlen = (ansicht: GespeicherteAnsicht) => {
    setAktiveAnsichtId(ansicht.ansicht_id);
    setDropdownOffen(false);
    onFilterChange(ansicht.filter_json);
  };

  const ansichtLoeschen = async (e: React.MouseEvent, ansichtId: number) => {
    e.stopPropagation();
    if (!window.confirm('Ansicht wirklich löschen?')) return;
    try {
      await axios.delete(`${API_URL}/api/ansichten/${ansichtId}`, { headers });
      if (aktiveAnsichtId === ansichtId) {
        setAktiveAnsichtId(null);
      }
      await ladeAnsichten();
    } catch {
      alert('Löschen fehlgeschlagen');
    }
  };

  const dialogOeffnen = () => {
    setNeuerName('');
    setIstStandard(false);
    setDialogOffen(true);
    setDropdownOffen(false);
  };

  const ansichtSpeichern = async () => {
    if (!neuerName.trim()) return;
    setSpeichernLaeuft(true);
    try {
      const res = await axios.post<GespeicherteAnsicht>(
        `${API_URL}/api/ansichten`,
        {
          bereich,
          name: neuerName.trim(),
          filter_json: currentFilter ?? {},
          ist_standard: istStandard,
        },
        { headers }
      );
      setAktiveAnsichtId(res.data.ansicht_id);
      setDialogOffen(false);
      await ladeAnsichten();
    } catch {
      alert('Speichern fehlgeschlagen');
    } finally {
      setSpeichernLaeuft(false);
    }
  };

  const ansichtZuruecksetzen = () => {
    setAktiveAnsichtId(null);
    onFilterChange({});
    setDropdownOffen(false);
  };

  return (
    <div className="vs-root">
      <div className="vs-dropdown-wrap" ref={dropdownRef}>
        <button
          className={`vs-trigger ${dropdownOffen ? 'vs-trigger--open' : ''}`}
          onClick={() => setDropdownOffen(o => !o)}
          title="Gespeicherte Ansichten"
        >
          <span className="vs-icon">&#9776;</span>
          <span className="vs-label">
            {aktiveAnsicht ? aktiveAnsicht.name : 'Ansicht'}
          </span>
          <span className="vs-caret">&#9660;</span>
        </button>

        {dropdownOffen && (
          <div className="vs-dropdown">
            {ladeFehler && (
              <div className="vs-error">{ladeFehler}</div>
            )}

            {ansichten.length === 0 && !ladeFehler && (
              <div className="vs-empty">Keine gespeicherten Ansichten</div>
            )}

            {aktiveAnsichtId !== null && (
              <button className="vs-item vs-item--reset" onClick={ansichtZuruecksetzen}>
                <span className="vs-item-icon">&#215;</span>
                <span>Ansicht zurücksetzen</span>
              </button>
            )}

            {ansichten.map(ansicht => (
              <div
                key={ansicht.ansicht_id}
                className={`vs-item ${ansicht.ansicht_id === aktiveAnsichtId ? 'vs-item--active' : ''}`}
                onClick={() => ansichtWaehlen(ansicht)}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && ansichtWaehlen(ansicht)}
              >
                <span className="vs-item-name">
                  {ansicht.ist_standard && <span className="vs-standard-dot" title="Standard">&#9679;</span>}
                  {ansicht.name}
                </span>
                <button
                  className="vs-item-del"
                  onClick={e => ansichtLoeschen(e, ansicht.ansicht_id)}
                  title="Löschen"
                  tabIndex={-1}
                >
                  &#215;
                </button>
              </div>
            ))}

            <div className="vs-dropdown-sep" />

            <button className="vs-item vs-item--save" onClick={dialogOeffnen}>
              <span className="vs-item-icon">+</span>
              <span>Aktuelle Ansicht speichern</span>
            </button>
          </div>
        )}
      </div>

      {/* Speichern-Dialog */}
      {dialogOffen && (
        <div className="vs-dialog-overlay" onClick={() => setDialogOffen(false)}>
          <div className="vs-dialog" onClick={e => e.stopPropagation()}>
            <div className="vs-dialog-header">
              <span>Ansicht speichern</span>
              <button className="vs-dialog-close" onClick={() => setDialogOffen(false)}>&#215;</button>
            </div>

            <div className="vs-dialog-body">
              <label className="vs-dialog-label">
                Name
                <input
                  className="vs-dialog-input"
                  type="text"
                  value={neuerName}
                  onChange={e => setNeuerName(e.target.value)}
                  placeholder="z.B. Offene Angebote"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && ansichtSpeichern()}
                />
              </label>

              <label className="vs-dialog-checkbox">
                <input
                  type="checkbox"
                  checked={istStandard}
                  onChange={e => setIstStandard(e.target.checked)}
                />
                Als Standard für diesen Bereich setzen
              </label>
            </div>

            <div className="vs-dialog-footer">
              <button className="vs-btn vs-btn--ghost" onClick={() => setDialogOffen(false)}>
                Abbrechen
              </button>
              <button
                className="vs-btn vs-btn--primary"
                onClick={ansichtSpeichern}
                disabled={!neuerName.trim() || speichernLaeuft}
              >
                {speichernLaeuft ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewSelector;
